import { LEGACY_MANUAL_PAYMENT_LABEL } from '@pos/types';
import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { Suspense, useMemo, useState } from 'react';

import { Card } from '../../components/Card';
import { BarCompare, DonutShare } from '../../components/charts';
import { ExportMenu } from '../../components/ExportMenu';
import { FilterBar } from '../../components/FilterBar';
import { KpiCard } from '../../components/KpiCard';
import { Select } from '../../components/Select';
import { DataTable } from '../../components/Table';
import { getDailyAnalytics, getDailySales } from '../../data/fetchers';
import { CHART_H } from '../../lib/chartTokens';
import type { ExportCol } from '../../lib/export';
import {
  fmtAmount,
  fmtBaht,
  fmtBahtShort,
  fmtDate,
  fmtInt,
  fmtPct,
  fmtSatangCompact,
} from '../../lib/format';
import { useBranchScope } from '../../lib/useBranchScope';
import { useDateRange } from '../../lib/useDateRange';
import type { SaleTxn } from '../../types/domain';
import { ReportDateFilter } from './ReportDateFilter';
import { ReportHeader } from './ReportHeader';

const deltaFrac = (cur: number, prev: number): number =>
  prev > 0 ? (cur - prev) / prev : cur > 0 ? 1 : 0;
const chartSkeleton = (
  <div
    className="animate-pulse bg-bg-warm rounded-card"
    style={{ height: CHART_H.md }}
  />
);

// --- per-day breakdown table ---
interface DayRow {
  day: string;
  revenue: number;
  dod: number | null;
  share: number;
}
interface DayExportRow {
  day: string;
  revenueBaht: string;
  revenueSatang: number;
  dodPct: string;
  sharePct: string;
}
type MethodFilter =
  'all' | 'cash' | 'qr_gateway' | 'qr_scb' | 'qr_personal' | 'reference_manual';
const paymentMethodLabel = (method: string): string => {
  if (method === 'cash') return 'เงินสด';
  if (method === 'qr_gateway' || method === 'qr_scb') return 'QR นิติ-SCB';
  if (method === 'qr' || method === 'qr_personal') return 'พร้อมเพย์ส่วนบุคคล';
  if (method === 'reference_manual') return LEGACY_MANUAL_PAYMENT_LABEL;
  return method;
};
const normalizePaymentMethod = (
  method: string,
): Exclude<MethodFilter, 'all'> => {
  if (
    method === 'cash' ||
    method === 'qr_personal' ||
    method === 'reference_manual'
  )
    return method;
  if (method === 'qr_gateway' || method === 'qr_scb') return 'qr_gateway';
  if (method === 'qr') return 'qr_personal';
  return 'reference_manual';
};
const effectiveMethods = (t: SaleTxn): Exclude<MethodFilter, 'all'>[] => {
  const methods = t.payments?.length
    ? t.payments
        .filter(
          (p) =>
            p.status === undefined ||
            p.status === 'captured' ||
            p.status === 'confirmed',
        )
        .map((p) => normalizePaymentMethod(p.method))
    : [];
  const fallback =
    methods.length > 0 ? methods : [normalizePaymentMethod(t.method)];
  return Array.from(new Set(fallback));
};
const effectivePaymentLabels = (t: SaleTxn): string[] => {
  const labels = (t.payments ?? [])
    .filter(
      (payment) =>
        payment.status === undefined ||
        payment.status === 'captured' ||
        payment.status === 'confirmed',
    )
    .map((payment) =>
      normalizePaymentMethod(payment.method) === 'reference_manual' &&
      payment.paymentLabel?.trim()
        ? payment.paymentLabel.trim()
        : paymentMethodLabel(payment.method),
    );
  return labels.length > 0 ? labels : [paymentMethodLabel(t.method)];
};
const DodCell = ({ v }: { v: number | null }) => {
  if (v == null) return <span className="text-ink-4">—</span>;
  const cls = v > 0.001 ? 'text-ok' : v < -0.001 ? 'text-danger' : 'text-ink-4';
  return (
    <span className={`num font-bold ${cls}`}>
      {v > 0 ? '▲ +' : v < 0 ? '▼ ' : '– '}
      {Math.abs(v * 100).toFixed(1)}%
    </span>
  );
};
const dayColumns: ColumnDef<DayRow, unknown>[] = [
  { accessorKey: 'day', header: 'วันที่' },
  {
    accessorKey: 'revenue',
    header: 'ยอดขาย',
    cell: (c) => <span className="num">{fmtAmount(c.getValue<number>())}</span>,
  },
  {
    accessorKey: 'dod',
    header: 'Δ วันก่อนหน้า',
    cell: (c) => <DodCell v={c.getValue<number | null>()} />,
  },
  {
    accessorKey: 'share',
    header: 'สัดส่วนช่วงนี้',
    cell: (c) => (
      <span className="num">{fmtPct(c.getValue<number>() * 100)}</span>
    ),
  },
];
const dayExportCols: ExportCol<DayExportRow>[] = [
  { key: 'day', header: 'วันที่' },
  { key: 'revenueBaht', header: 'ยอดขาย' },
  { key: 'revenueSatang', header: 'ยอดขาย (satang)' },
  { key: 'dodPct', header: 'Δ วันก่อนหน้า' },
  { key: 'sharePct', header: 'สัดส่วนช่วงนี้' },
];

// --- transaction sample table (real txns) ---
interface SalesRow {
  id: string;
  date: string;
  items: number;
  method: string;
  status: string;
  total: number;
}
const txnColumns: ColumnDef<SalesRow, unknown>[] = [
  { accessorKey: 'id', header: 'เลขบิล' },
  { accessorKey: 'date', header: 'วันที่' },
  {
    accessorKey: 'items',
    header: 'รายการ',
    cell: (c) => <span className="num">{fmtInt(c.getValue<number>())}</span>,
  },
  { accessorKey: 'method', header: 'วิธีชำระ' },
  { accessorKey: 'status', header: 'สถานะ' },
  {
    accessorKey: 'total',
    header: 'ยอด',
    cell: (c) => <span className="num">{fmtAmount(c.getValue<number>())}</span>,
  },
];
const toRow = (t: SaleTxn): SalesRow => ({
  id: t.id,
  date: fmtDate(t.at),
  items: t.items.length,
  method: effectivePaymentLabels(t).join(' + '),
  status: t.status === 'refund' ? 'คืนเงิน' : 'ปกติ',
  total: t.total,
});

export function DailySalesView() {
  const { scope, ready } = useBranchScope();
  const { range, prev } = useDateRange();
  const { data, isLoading } = useQuery({
    queryKey: [
      'report',
      'daily',
      scope.branchId,
      scope.allowedBranchIds,
      range.fromDay,
      range.toDay,
    ],
    queryFn: () => getDailyAnalytics(scope, range, prev),
    enabled: ready,
  });
  const { data: txnData } = useQuery({
    queryKey: [
      'report',
      'daily-txns',
      scope.branchId,
      scope.allowedBranchIds,
      range.fromDay,
      range.toDay,
    ],
    queryFn: () => getDailySales(scope, range),
    enabled: ready,
  });

  const [method, setMethod] = useState<MethodFilter>('all');
  const [status, setStatus] = useState<'all' | 'normal' | 'refund'>('all');

  const total = data?.total ?? 0;
  const days = data?.days ?? 1;
  const totalDelta = deltaFrac(total, data?.prevTotal ?? 0);

  const dayRows = useMemo<DayRow[]>(
    () =>
      (data?.series ?? []).map((s, i, arr) => ({
        day: s.label,
        revenue: s.value,
        dod: i > 0 ? deltaFrac(s.value, arr[i - 1].value) : null,
        share: total ? s.value / total : 0,
      })),
    [data, total],
  );
  const dayExportRows = useMemo<DayExportRow[]>(
    () =>
      dayRows.map((r) => ({
        day: r.day,
        revenueBaht: fmtBaht(r.revenue),
        revenueSatang: r.revenue,
        dodPct: r.dod == null ? '—' : fmtPct(r.dod * 100),
        sharePct: fmtPct(r.share * 100),
      })),
    [dayRows],
  );

  const txns = txnData?.txns ?? [];
  const filtered = txns.filter(
    (t) =>
      (method === 'all' || effectiveMethods(t).includes(method)) &&
      (status === 'all' || t.status === status),
  );
  const txnRows = useMemo(() => filtered.map(toRow), [filtered]);

  return (
    <div className="bo-fade">
      <ReportHeader
        title="ยอดขายรายวัน"
        subtitle="แนวโน้มรายวัน · รูปแบบรายชั่วโมง · การชำระเงิน และรายการขาย"
        status={{
          label:
            !ready || isLoading
              ? 'กำลังประมวลผล'
              : `${dayRows.length} วันในรายงาน`,
          tone: !ready || isLoading ? 'neutral' : 'ok',
        }}
        actions={
          <ExportMenu
            rows={dayExportRows}
            cols={dayExportCols}
            filename="ยอดขายรายวัน"
            title="ยอดขายรายวัน"
            pdf
          />
        }
      />
      <ReportDateFilter />

      <section aria-labelledby="daily-summary-heading" className="mb-6">
        <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
          <div>
            <h2
              id="daily-summary-heading"
              className="text-h3 font-bold text-ink"
            >
              ภาพรวมช่วงที่เลือก
            </h2>
            <p className="mt-1 text-sm text-ink-3">
              ยอดสุทธิ แนวโน้ม และวันที่ทำยอดสูงสุด
            </p>
          </div>
          <span className="text-sm-label text-ink-3">{days} วัน</span>
        </div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <KpiCard
            label="ยอดขายรวม"
            value={fmtBaht(total)}
            tone="primary"
            delta={totalDelta}
            sub="เทียบงวดก่อน"
          />
          <KpiCard
            label="เฉลี่ย/วัน"
            value={fmtBaht(days ? total / days : 0)}
            tone="accent"
            sub={`${days} วัน`}
          />
          <KpiCard
            label="วันขายสูงสุด"
            value={data?.bestDay ? `วันที่ ${data.bestDay.label}` : '—'}
            tone="ok"
            sub={data?.bestDay ? fmtBahtShort(data.bestDay.value) : undefined}
          />
        </div>
      </section>

      <section aria-labelledby="recent-receipts-heading" className="mb-6">
        <div className="mb-3 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <h2
              id="recent-receipts-heading"
              className="text-h3 font-bold text-ink"
            >
              บิลล่าสุด
            </h2>
            <p className="mt-1 text-sm-label text-ink-3">
              รายการล่าสุดบางส่วน — ไม่ใช่ผลรวมของช่วงที่เลือก
            </p>
          </div>
          <FilterBar>
            <label className="flex w-full min-w-0 flex-col gap-1.5 sm:w-auto sm:min-w-40">
              <span className="text-sm-label font-semibold text-ink-2">
                วิธีชำระ
              </span>
              <Select
                aria-label="วิธีชำระ"
                value={method}
                onChange={(e) => setMethod(e.target.value as MethodFilter)}
              >
                <option value="all">ทั้งหมด</option>
                <option value="cash">เงินสด</option>
                <option value="qr_gateway">QR นิติ-SCB</option>
                <option value="qr_personal">พร้อมเพย์ส่วนบุคคล</option>
                <option value="reference_manual">
                  {LEGACY_MANUAL_PAYMENT_LABEL}
                </option>
              </Select>
            </label>
            <label className="flex w-full min-w-0 flex-col gap-1.5 sm:w-auto sm:min-w-40">
              <span className="text-sm-label font-semibold text-ink-2">
                สถานะ
              </span>
              <Select
                aria-label="สถานะ"
                value={status}
                onChange={(e) =>
                  setStatus(e.target.value as 'all' | 'normal' | 'refund')
                }
              >
                <option value="all">ทั้งหมด</option>
                <option value="normal">ปกติ</option>
                <option value="refund">คืนเงิน</option>
              </Select>
            </label>
          </FilterBar>
        </div>
        <DataTable
          pageSize={Number.MAX_SAFE_INTEGER}
          columns={txnColumns}
          data={txnRows}
          emptyMessage="ไม่พบบิลในช่วงเวลาที่เลือก"
          mobileCards
        />
      </section>

      <section aria-labelledby="daily-insights-heading" className="space-y-6">
        <div>
          <h2
            id="daily-insights-heading"
            className="text-h3 font-bold text-ink"
          >
            วิเคราะห์ยอดขาย
          </h2>
          <p className="mt-1 text-sm text-ink-3">
            เปรียบเทียบแนวโน้มรายวันและช่องทางรับชำระ
          </p>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Card title="แนวโน้มยอดขายรายวัน" className="lg:col-span-2">
            <Suspense fallback={chartSkeleton}>
              <BarCompare
                data={data?.series ?? []}
                valueFormat={fmtBahtShort}
                tickFormat={fmtSatangCompact}
              />
            </Suspense>
          </Card>
          <Card title="สัดส่วนการชำระเงิน (บาท)">
            <Suspense fallback={chartSkeleton}>
              <DonutShare
                data={data?.payment ?? []}
                valueFormat={fmtBahtShort}
              />
            </Suspense>
          </Card>
        </div>

        <div>
          <h3 className="mb-3 text-h3 font-bold text-ink">ยอดขายแยกตามวัน</h3>
          {!ready || isLoading ? (
            <div className="bo-card h-40 animate-pulse" />
          ) : (
            <DataTable
              pageSize={Number.MAX_SAFE_INTEGER}
              columns={dayColumns}
              data={dayRows}
              mobileCards
            />
          )}
        </div>

        {(data?.byHour?.length ?? 0) > 0 && (
          <Card title="ช่วงเวลาที่ขายดี (เฉลี่ยต่อวัน)">
            <Suspense fallback={chartSkeleton}>
              <BarCompare
                data={data!.byHour}
                valueFormat={fmtBahtShort}
                tickFormat={fmtSatangCompact}
              />
            </Suspense>
          </Card>
        )}
      </section>
    </div>
  );
}
